import Alert from '@mui/material/Alert';
import Divider from '@mui/material/Divider';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import React, { type JSX } from 'react';

import {
  getReceiptDateLabel,
  getReceiptLineItemLabel,
  getReceiptSummaryItems,
  getReceiptTitle,
  RECEIPT_DETAILS_READ_ONLY_MESSAGE,
  type Receipt,
} from 'app-components/flux/receipts';

import ReceiptImage from 'components/Receipts/ReceiptImage';
import useFormatAsCurrency from 'hooks/useFormatAsCurrency';

type Props = {
  receipt: Receipt;
  imageHeight?: number;
};

const ReceiptDetails = ({ receipt, imageHeight = 408 }: Props): JSX.Element => {
  const formatAsCurrency = useFormatAsCurrency();
  const title = getReceiptTitle(receipt);
  const total = receipt.extraction?.total;
  const lineItems = receipt.extraction?.lineItems ?? [];
  const summaryItems = getReceiptSummaryItems(receipt);
  const dateLabel = getReceiptDateLabel(receipt);

  return (
    <Stack spacing={3}>
      <ReceiptImage
        documentId={receipt.documentId}
        localFileUri={receipt.localFileUri}
        mimeType={receipt.mimeType}
        preserveLocalPreview
        alt={`${title} receipt`}
        width='100%'
        height={imageHeight}
        objectFit='contain'
        borderRadius={1.5}
      />

      <Alert severity='info'>{RECEIPT_DETAILS_READ_ONLY_MESSAGE}</Alert>

      <Stack spacing={2}>
        <Stack spacing={0.5}>
          <Stack direction='row' alignItems='baseline' justifyContent='space-between' gap={2}>
            <Typography variant='paragraphMedium' color='text.primary'>
              {title}
            </Typography>
            <Typography
              variant='paragraphMedium'
              color='text.primary'
              sx={{ flexShrink: 0, fontVariantNumeric: 'tabular-nums' }}
            >
              {total === undefined ? '—' : formatAsCurrency(total, { signDisplay: 'auto' })}
            </Typography>
          </Stack>
          {dateLabel ? (
            <Typography variant='captionRegular' color='text.secondary'>
              {dateLabel}
            </Typography>
          ) : null}
        </Stack>

        {lineItems.length > 0 ? (
          <Stack spacing={0.5} aria-label='Receipt line items'>
            {lineItems.map((lineItem, index) => (
              <Stack
                key={`${lineItem.description}-${index}`}
                direction='row'
                alignItems='baseline'
                justifyContent='space-between'
                gap={2}
              >
                <Typography variant='captionRegular' color='text.secondary'>
                  {getReceiptLineItemLabel(lineItem)}
                </Typography>
                <Typography
                  variant='captionRegular'
                  color='text.secondary'
                  sx={{ flexShrink: 0, fontVariantNumeric: 'tabular-nums' }}
                >
                  {lineItem.amount === undefined ? '—' : formatAsCurrency(lineItem.amount, { signDisplay: 'auto' })}
                </Typography>
              </Stack>
            ))}
          </Stack>
        ) : (
          <Typography variant='captionRegular' color='text.secondary'>
            No line items were extracted.
          </Typography>
        )}

        {summaryItems.length > 0 ? (
          <Stack spacing={2} aria-label='Receipt totals'>
            <Divider />
            <Stack spacing={0.5}>
              {summaryItems.map((summaryItem) => (
                <Stack
                  key={summaryItem.label}
                  direction='row'
                  alignItems='baseline'
                  justifyContent='space-between'
                  gap={2}
                >
                  <Typography variant='captionRegular' color='text.secondary'>
                    {summaryItem.label}
                  </Typography>
                  <Typography
                    variant='captionRegular'
                    color='text.secondary'
                    sx={{ flexShrink: 0, fontVariantNumeric: 'tabular-nums' }}
                  >
                    {formatAsCurrency(summaryItem.amount, { signDisplay: 'auto' })}
                  </Typography>
                </Stack>
              ))}
            </Stack>
          </Stack>
        ) : null}
      </Stack>
    </Stack>
  );
};

export default ReceiptDetails;
